import { useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { fetchJson } from '../../lib/http';
import { bundledLinkPreview, messagePreviewUrls, type LinkPreview } from '../../lib/link-previews';

export function LinkPreviews({ body }: { body: string }) {
  return <>{messagePreviewUrls(body).map(url => <Preview key={url} url={url} />)}</>;
}

function Preview({ url }: { url: string }) {
  const [preview, setPreview] = useState(() => bundledLinkPreview(url));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [hidden, setHidden] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  if (hidden) return null;
  const load = async () => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      const result = await fetchJson<LinkPreview>('/v1/link-previews', { method: 'POST', body: { url } });
      // Images from outside websites are fetched by the guarded server, never
      // directly by the reader's browser. Do not accept a remote image URL here.
      setPreview({ ...result, image: result.image?.match(/^data:image\/(?:png|jpeg|webp);base64,[A-Za-z0-9+/=]+$/) ? result.image : null });
    } catch { setError('Preview unavailable. You can still open the link above.'); }
    finally { setBusy(false); }
  };
  return <aside aria-label={`Website preview: ${new URL(url).hostname}`} className="my-2 w-full max-w-md overflow-hidden rounded-xl border border-cloud-200 border-l-[3px] border-l-strawberry-400 bg-cloud-50/70 text-sm">
    <div className="flex items-center justify-between px-3 pt-2 text-[11px] text-cloud-500">
      <span>{preview?.site ?? new URL(url).hostname}</span>
      <button type="button" aria-label="Hide website preview" onClick={() => setHidden(true)} className="rounded px-2 py-1 hover:bg-cloud-100">×</button>
    </div>
    {preview ? <>
      <a href={url} target="_blank" rel="noopener noreferrer" className="block px-3 pb-3">
        <span className="font-semibold text-strawberry-700">{preview.title} <ExternalLink className="inline h-3 w-3" /></span>
        {preview.description && <p className="mt-1 line-clamp-3 text-xs leading-5 text-cloud-600">{preview.description}</p>}
      </a>
      {preview.image && !imageFailed && <a href={url} target="_blank" rel="noopener noreferrer" tabIndex={-1} aria-hidden><img src={preview.image} alt="" loading="lazy" referrerPolicy="no-referrer" onError={() => setImageFailed(true)} className="max-h-56 w-full border-t border-cloud-200 object-cover" /></a>}
    </> : <div className="px-3 pb-3">
      <p className="mb-2 text-xs leading-5 text-cloud-500">Loading shares this URL with Link’s server, which fetches the website and preview image. The rest of your message is not sent.</p>
      <button type="button" onClick={() => void load()} disabled={busy} className="rounded-lg border border-cloud-200 bg-white px-3 py-1.5 text-xs font-semibold text-cloud-700 disabled:opacity-50">{busy ? 'Loading preview…' : 'Load preview'}</button>
      {error && <p role="status" className="mt-2 text-xs text-cloud-500">{error}</p>}
    </div>}
  </aside>;
}
